import { useEffect, useState } from 'react';
import { api } from '../lib/api';

type ProviderType = 'stripe' | 'paypal';
type ProviderMode = 'test' | 'live';

interface ProviderRow {
  id: number | null;
  type: ProviderType;
  mode: ProviderMode;
  label: string;
  is_active: boolean;
  implemented: boolean;
  has_publishable_key?: boolean;
  has_secret_key?: boolean;
  has_webhook_secret?: boolean;
  has_client_id?: boolean;
  has_client_secret?: boolean;
  has_webhook_id?: boolean;
  publishable_key?: string;
  client_id?: string;
  last_tested_at?: string | null;
  last_test_ok?: boolean | null;
  last_test_error?: string | null;
}

const MASK = '••••••••••••';

export function Billing() {
  const [providers, setProviders] = useState<ProviderRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const data = await api<{ providers: ProviderRow[] }>('/admin/billing/providers');
      setProviders(data.providers);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div>
      <h1>Billing</h1>
      <p className="subtitle">
        Configure payment providers and their credentials. Exactly one provider can be active
        at a time — the active one is used for live checkouts and webhook verification.
      </p>
      {error && <div className="banner err">{error}</div>}
      {info && <div className="banner ok">{info}</div>}

      {!providers ? (
        <div className="muted">Loading…</div>
      ) : (
        providers.map((p) => (
          <ProviderCard
            key={`${p.type}:${p.mode}`}
            row={p}
            onSaved={(msg) => {
              setInfo(msg);
              setTimeout(() => setInfo(null), 4000);
              load();
            }}
            onError={(msg) => {
              setError(msg);
              setTimeout(() => setError(null), 6000);
            }}
          />
        ))
      )}

      <PricingHealthPanel
        onInfo={(msg) => {
          setInfo(msg);
          setTimeout(() => setInfo(null), 4000);
        }}
        onError={(msg) => {
          setError(msg);
          setTimeout(() => setError(null), 6000);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PricingHealthPanel
//
// Surfaces GET /admin/billing/drift in the SPA. Each row is one active
// admin product alongside its live Stripe state. Drifted rows render with
// a red badge + the specific reasons (e.g. "unit_amount: db=4900
// stripe=2900") and a "Republish to Stripe" button that POSTs to
// /admin/billing/products/:id/republish (the new drift-aware sync helper).
//
// Why this exists: caught by Craig E2E 2026-06-01 — admin DB said $49 CAD
// but Stripe checkout quoted $29. The drift detector now logs at admin-cp
// boot AND surfaces here so operators don't have to know to grep logs or
// curl the API.
// ---------------------------------------------------------------------------

interface DriftRow {
  product_id: number;
  product_slug: string;
  product_name: string;
  db: { price_cents: number; currency: string; billing_interval: string };
  provider_link: {
    provider_product_id: string | null;
    provider_price_id: string | null;
    synced_at: string | null;
    last_sync_error: string | null;
  } | null;
  stripe: {
    product_name: string | null;
    unit_amount: number | null;
    currency: string | null;
    recurring_interval: string | null;
    active: boolean | null;
    fetch_error: string | null;
  } | null;
  reasons: string[];
}

function fmtMoney(cents: number | null | undefined, currency: string | null | undefined) {
  if (cents == null || !currency) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function PricingHealthPanel({
  onInfo,
  onError,
}: {
  onInfo: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [data, setData] = useState<{ rows: DriftRow[]; total: number; drifted: number } | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [republishing, setRepublishing] = useState<number | null>(null);

  async function load() {
    setLoadErr(null);
    try {
      const r = await api<{ rows: DriftRow[]; total: number; drifted: number }>(
        '/admin/billing/drift',
      );
      setData(r);
    } catch (e) {
      setLoadErr((e as Error).message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function republish(productId: number, slug: string) {
    setRepublishing(productId);
    try {
      await api(`/admin/billing/products/${productId}/republish`, { method: 'POST' });
      onInfo(`${slug} republished to Stripe.`);
      await load();
    } catch (e) {
      onError(`Republish failed for ${slug}: ${(e as Error).message}`);
    } finally {
      setRepublishing(null);
    }
  }

  return (
    <div className="card" style={{ marginTop: 24, padding: 16 }}>
      <h2 style={{ marginTop: 0, fontSize: 18 }}>Pricing health</h2>
      <p className="muted small" style={{ marginTop: 4 }}>
        Compares every active admin product to its live Stripe product/price. Caught by the same
        boot-time startup check (<code>billing.products.stripe_in_sync</code>).
      </p>
      {loadErr && <div className="banner err">{loadErr}</div>}
      {!data ? (
        <div className="muted">Loading drift report…</div>
      ) : data.rows.length === 0 ? (
        <div className="muted">No active products configured yet.</div>
      ) : data.drifted === 0 ? (
        <div className="banner ok">
          ✓ All {data.total} active product{data.total === 1 ? '' : 's'} match Stripe.
        </div>
      ) : (
        <>
          <div className="banner err">
            {data.drifted} of {data.total} product{data.total === 1 ? '' : 's'} {data.drifted === 1 ? 'is' : 'are'} out of sync with Stripe.
          </div>
          {data.rows
            .filter((r) => r.reasons.length > 0)
            .map((r) => (
              <DriftRowCard
                key={r.product_id}
                row={r}
                onRepublish={() => republish(r.product_id, r.product_slug)}
                busy={republishing === r.product_id}
              />
            ))}
        </>
      )}
      {data && data.rows.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary className="muted small" style={{ cursor: 'pointer' }}>
            Show full status ({data.total} product{data.total === 1 ? '' : 's'})
          </summary>
          <div style={{ marginTop: 8 }}>
            {data.rows.map((r) => (
              <DriftRowCard
                key={r.product_id}
                row={r}
                onRepublish={() => republish(r.product_id, r.product_slug)}
                busy={republishing === r.product_id}
                compact
              />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function DriftRowCard({
  row,
  onRepublish,
  busy,
  compact,
}: {
  row: DriftRow;
  onRepublish: () => void;
  busy: boolean;
  compact?: boolean;
}) {
  const drifted = row.reasons.length > 0;
  return (
    <div
      className="card"
      style={{
        marginTop: 12,
        padding: compact ? 10 : 14,
        background: drifted ? '#fff5f5' : '#f9fafb',
        borderColor: drifted ? '#fca5a5' : undefined,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <div>
          <strong>{row.product_name}</strong>{' '}
          <span className="muted small">({row.product_slug})</span>
        </div>
        <span className={drifted ? 'pill err' : 'pill ok'}>{drifted ? 'DRIFT' : 'in sync'}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8, fontSize: 13 }}>
        <div>
          <div className="muted small">Admin DB</div>
          <div>
            {fmtMoney(row.db.price_cents, row.db.currency)} / {row.db.billing_interval}
          </div>
        </div>
        <div>
          <div className="muted small">Stripe</div>
          <div>
            {row.stripe
              ? `${fmtMoney(row.stripe.unit_amount, row.stripe.currency)} / ${row.stripe.recurring_interval ?? 'one_time'}${row.stripe.active === false ? ' (archived)' : ''}`
              : row.provider_link
                ? '—'
                : 'not yet published'}
          </div>
        </div>
      </div>
      {drifted && (
        <>
          <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 18, fontSize: 13 }}>
            {row.reasons.map((reason, i) => (
              <li key={i}>{reason}</li>
            ))}
          </ul>
          <button onClick={onRepublish} disabled={busy}>
            {busy ? 'Republishing…' : 'Republish to Stripe'}
          </button>
        </>
      )}
    </div>
  );
}

function ProviderCard({
  row,
  onSaved,
  onError,
}: {
  row: ProviderRow;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  // Form-local state; pre-fill safe fields, mask secrets.
  const [publishableKey, setPublishableKey] = useState(row.publishable_key ?? '');
  const [secretKey, setSecretKey] = useState(row.has_secret_key ? MASK : '');
  const [webhookSecret, setWebhookSecret] = useState(row.has_webhook_secret ? MASK : '');
  const [clientId, setClientId] = useState(row.client_id ?? '');
  const [clientSecret, setClientSecret] = useState(row.has_client_secret ? MASK : '');
  const [webhookId, setWebhookId] = useState(row.has_webhook_id ? MASK : '');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [activating, setActivating] = useState(false);

  const notImplemented = !row.implemented;

  async function save() {
    if (notImplemented) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = { type: row.type, mode: row.mode };
      if (row.type === 'stripe') {
        if (publishableKey) body.publishable_key = publishableKey;
        if (secretKey && secretKey !== MASK) body.secret_key = secretKey;
        if (webhookSecret && webhookSecret !== MASK) body.webhook_secret = webhookSecret;
      } else {
        if (clientId) body.client_id = clientId;
        if (clientSecret && clientSecret !== MASK) body.client_secret = clientSecret;
        if (webhookId && webhookId !== MASK) body.webhook_id = webhookId;
      }
      await api('/admin/billing/providers', { method: 'PUT', json: body });
      onSaved(`${row.label}: saved`);
    } catch (e) {
      onError(`${row.label}: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    if (row.id === null) {
      onError('Save the credentials first, then test.');
      return;
    }
    setTesting(true);
    try {
      const r = await api<{ ok: boolean; detail?: string; error?: string }>(
        `/admin/billing/providers/${row.id}/test`,
        { method: 'POST' },
      );
      if (r.ok) {
        onSaved(`${row.label}: ${r.detail ?? 'OK'}`);
      } else {
        onError(`${row.label}: ${r.error ?? 'unknown error'}`);
      }
    } catch (e) {
      onError(`${row.label}: ${(e as Error).message}`);
    } finally {
      setTesting(false);
    }
  }

  async function activate() {
    if (row.id === null) {
      onError('Save the credentials first, then activate.');
      return;
    }
    if (
      !confirm(
        row.mode === 'live'
          ? `Switching to ${row.label} will route real customer payments. Continue?`
          : `Set ${row.label} as the active provider?`,
      )
    ) {
      return;
    }
    setActivating(true);
    try {
      await api(`/admin/billing/providers/${row.id}/activate`, { method: 'POST' });
      onSaved(`${row.label}: activated`);
    } catch (e) {
      onError(`${row.label}: ${(e as Error).message}`);
    } finally {
      setActivating(false);
    }
  }

  const testResultPill =
    row.last_tested_at && row.last_test_ok === true
      ? (
        <span className="pill ok" title={`Tested ${new Date(row.last_tested_at).toLocaleString()}`}>
          tested OK
        </span>
      )
      : row.last_tested_at && row.last_test_ok === false
      ? (
        <span className="pill err" title={row.last_test_error ?? ''}>
          test failed
        </span>
      )
      : null;

  return (
    <div className="card">
      <div className="card-header">
        <h3>{row.label}</h3>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {row.is_active && <span className="pill active">ACTIVE</span>}
          {testResultPill}
          {notImplemented && <span className="pill muted">future version</span>}
        </div>
      </div>

      {notImplemented && (
        <div className="banner info">
          PayPal integration isn't wired up yet — saving here is disabled. We'll add it once you're
          ready to support a second payment method.
        </div>
      )}

      {row.type === 'stripe' ? (
        <>
          <div className="form-row">
            <div>
              <label>Publishable key</label>
              <input
                value={publishableKey}
                onChange={(e) => setPublishableKey(e.target.value)}
                placeholder={row.mode === 'test' ? 'pk_test_…' : 'pk_live_…'}
                disabled={notImplemented || saving}
              />
              <div className="helper">Safe to expose; used by the signup page if you ever switch to Stripe Elements.</div>
            </div>
            <div>
              <label>Secret key</label>
              <input
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder={row.mode === 'test' ? 'sk_test_…' : 'sk_live_…'}
                disabled={notImplemented || saving}
                type="password"
                autoComplete="off"
              />
              <div className="helper">
                Stripe dashboard → Developers → API keys. {row.has_secret_key && 'A value is stored; leave masked to keep it.'}
              </div>
            </div>
          </div>

          <label>Webhook signing secret</label>
          <input
            value={webhookSecret}
            onChange={(e) => setWebhookSecret(e.target.value)}
            placeholder="whsec_…"
            disabled={notImplemented || saving}
            type="password"
            autoComplete="off"
          />
          <div className="helper">
            Stripe dashboard → Developers → Webhooks → your endpoint → Signing secret.
            {row.has_webhook_secret && ' A value is stored; leave masked to keep it.'}
          </div>
        </>
      ) : (
        <>
          <div className="form-row">
            <div>
              <label>Client ID</label>
              <input value={clientId} disabled placeholder="(PayPal — future)" />
            </div>
            <div>
              <label>Client secret</label>
              <input value={clientSecret} disabled type="password" placeholder="(PayPal — future)" />
            </div>
          </div>
          <label>Webhook ID</label>
          <input value={webhookId} disabled type="password" placeholder="(PayPal — future)" />
        </>
      )}

      <div className="button-row">
        <button onClick={save} disabled={notImplemented || saving}>
          {saving ? 'Saving…' : 'Save credentials'}
        </button>
        <button
          className="secondary"
          onClick={test}
          disabled={notImplemented || testing || row.id === null}
          title={row.id === null ? 'Save first' : 'Verify credentials by hitting the provider'}
        >
          {testing ? 'Testing…' : 'Test connection'}
        </button>
        <div className="spacer" />
        {!row.is_active && !notImplemented && (
          <button
            className={row.mode === 'live' ? 'danger' : ''}
            onClick={activate}
            disabled={activating || row.id === null}
            title={row.id === null ? 'Save first' : ''}
          >
            {activating ? 'Activating…' : row.mode === 'live' ? 'Activate (live mode!)' : 'Make active'}
          </button>
        )}
      </div>

      {row.last_test_error && row.last_test_ok === false && (
        <div className="error-text" style={{ marginTop: 8 }}>
          Last test error: {row.last_test_error}
        </div>
      )}
    </div>
  );
}
